/**
 * Compute the FIRE number (portfolio size needed to retire).
 * @param {number} annualSpending - Annual spending target
 * @param {number} withdrawalRate - Safe withdrawal rate (e.g. 0.04)
 * @returns {number}
 */
export function computeFireNumber(annualSpending, withdrawalRate) {
  return annualSpending / withdrawalRate;
}

/**
 * Year-by-year projection until FIRE number is reached or maxYears exceeded.
 * @param {number} currentNetWorth
 * @param {number} annualSavings
 * @param {number} returnRate - Annual real return rate (e.g. 0.07)
 * @param {number} fireNumber
 * @param {number} maxYears
 * @returns {Array<{year: number, netWorth: number}>}
 */
export function computeFireProjection(currentNetWorth, annualSavings, returnRate, fireNumber, maxYears) {
  const data = [];
  let netWorth = currentNetWorth;
  for (let year = 0; year <= maxYears; year++) {
    data.push({ year, netWorth: Math.round(netWorth) });
    if (netWorth >= fireNumber) break;
    netWorth = netWorth * (1 + returnRate) + annualSavings;
  }
  return data;
}

/**
 * Returns the number of years until FIRE (first year netWorth >= fireNumber).
 * Returns null if never reached within the projection.
 * @param {Array<{year: number, netWorth: number}>} projectionArray
 * @param {number} fireNumber
 * @returns {number|null}
 */
export function computeYearsToFire(projectionArray, fireNumber) {
  const entry = projectionArray.find(d => d.netWorth >= fireNumber);
  return entry ? entry.year : null;
}

/**
 * Progress toward FIRE as a percentage, clamped 0–100.
 * @param {number} currentNetWorth
 * @param {number} fireNumber
 * @returns {number}
 */
export function computeProgressPercent(currentNetWorth, fireNumber) {
  if (!fireNumber || fireNumber <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((currentNetWorth / fireNumber) * 100)));
}
